import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useLanguage} from '../context/LanguageContext';
import {FaUsers,FaShieldAlt,FaBrain,FaHandshake,FaCheckCircle,FaArrowRight,FaHeart,FaLightbulb,FaUserMd,FaCogs,FaHome} from 'react-icons/fa';

const TreatmentCenters = () => {
  const {language} = useLanguage();

  const content = {
    en: {
      title: "COSMO Treatment Centers",
      subtitle: "Transforming Residential Care through Collaborative Assessment",
      intro: "COSMO supports treatment centers with innovative tools and a collaborative approach designed to address the complexity of working with adolescents and adults in residential settings. Many patients present histories of trauma, emotional dysregulation, and difficulties with interpersonal trust. These dynamics can strain the therapeutic relationship, leading to misunderstandings, frustration, and at times reactive or expulsive interventions.",
      
      sections: [
        {
          id: 'collaborative',
          icon: FaHandshake,
          title: "Collaborative Assessment: The Core of the Approach",
          text: "The COSMO model is grounded in Collaborative Assessment, which promotes an active and equal partnership between patient and clinical team.",
          bullets: [
            "Rorschach",
            "Picture Frustration Study (PFS)", 
            "Thurston-Cradock Test of Shame (TCTS)"
          ],
          subtext: "Teams can identify deep patterns in emotional functioning, interpersonal dynamics, and coping strategies. These insights become a true 'user manual for the patient', supporting the development of personalized and effective treatment plans."
        },
        {
          id: 'shame',
          icon: FaShieldAlt,
          title: "Understanding Shame and Vulnerability",
          text: "The Thurston-Cradock Test of Shame (TCTS) enables exploration of one of the most critical core issues: shame.",
          bullets: [
            "Patterns of emotional vulnerability",
            "Defensive strategies (withdrawal, aggression, contempt)"
          ],
          subtext: "This deeper understanding allows clinicians to address the root causes of behavior, fostering resilience and self-esteem."
        },
        {
          id: 'empathy',
          icon: FaHeart,
          title: "More Empathy, Less Countertransference",
          text: "COSMO helps teams to:",
          bullets: [
            "Reduce negative countertransference",
            "Avoid impulsive or punitive reactions",
            "Better understand the patient's inner world"
          ],
          subtext: "The result is an environment where patients feel understood, respected, and engaged, strengthening the therapeutic alliance."
        },
        {
          id: 'participation',
          icon: FaBrain,
          title: "The Patient as an Active Participant",
          text: "COSMO promotes direct patient involvement through:",
          bullets: [
            "Structured feedback sessions",
            "Guided reflection activities"
          ],
          additionalText: "This fosters:",
          additionalBullets: [
            "Greater self-awareness",
            "Improved emotional regulation skills", 
            "A stronger connection with the care team"
          ]
        },
        {
          id: 'team',
          icon: FaUsers,
          title: "Strengthening the Team, Improving Care",
          text: "COSMO works not only with patients, but also with clinical teams:",
          bullets: [
            "Targeted training",
            "Integration of clinical data (projective tests, self-report, observations)",
            "A shared framework among professionals"
          ],
          additionalText: "This leads to:",
          additionalBullets: [
            "Greater cohesion",
            "Alignment in intervention strategies",
            "Clarity in therapeutic goals"
          ]
        },
        {
          id: 'growth',
          icon: FaLightbulb,
          title: "From Behavior Management to Growth",
          text: "COSMO shifts the focus from managing behaviors to creating environments that support growth and transformation.",
          bullets: [
            "Accurate assessment",
            "Enhanced empathy",
            "Collaborative teamwork"
          ]
        }
      ],

      transformTitle: "Discover the COSMO Difference",
      transformText1: "With tools such as TCTS, Rorschach, and PFS, COSMO helps treatment centers to:",
      transformBullets: [
        "Build deeper connections with patients",
        "Improve clinical outcomes", 
        "Foster lasting change"
      ],
      
      ctaTitle: "Transform Your Treatment Center",
      ctaText: "Contact us to discover how COSMO can revolutionize your approach to residential care and create lasting positive change for your patients.",
      ctaButton: "Contact Our Team"
    },
    
    it: {
      title: "COSMO Centri di Trattamento",
      subtitle: "Trasformare la Cura Residenziale attraverso la Valutazione Collaborativa",
      intro: "COSMO supporta i centri di trattamento con strumenti innovativi e un approccio collaborativo progettato per affrontare la complessità del lavoro con adolescenti e adulti in contesti residenziali. Molti pazienti presentano storie di trauma, disregolazione emotiva e difficoltà con la fiducia interpersonale. Queste dinamiche possono mettere a dura prova la relazione terapeutica, portando a incomprensioni, frustrazione e a volte interventi reattivi o espulsivi.",
      
      sections: [
        {
          id: 'collaborative',
          icon: FaHandshake,
          title: "Valutazione Collaborativa: Il Nucleo dell'Approccio",
          text: "Il modello COSMO si basa sulla Valutazione Collaborativa, che promuove una partnership attiva e paritaria tra paziente e team clinico.",
          bullets: [
            "Rorschach",
            "Picture Frustration Study (PFS)",
            "Thurston-Cradock Test of Shame (TCTS)"
          ],
          subtext: "I team possono identificare modelli profondi nel funzionamento emotivo, nelle dinamiche interpersonali e nelle strategie di coping. Questi approfondimenti diventano un vero 'manuale d'uso per il paziente', supportando lo sviluppo di piani di trattamento personalizzati ed efficaci."
        },
        {
          id: 'shame',
          icon: FaShieldAlt,
          title: "Comprendere la Vergogna e la Vulnerabilità",
          text: "Il Thurston-Cradock Test of Shame (TCTS) consente l'esplorazione di una delle questioni centrali più critiche: la vergogna.",
          bullets: [
            "Modelli di vulnerabilità emotiva",
            "Strategie difensive (ritiro, aggressione, disprezzo)"
          ],
          subtext: "Questa comprensione più profonda consente ai clinici di affrontare le cause profonde del comportamento, favorendo resilienza e autostima."
        },
        {
          id: 'empathy',
          icon: FaHeart,
          title: "Più Empatia, Meno Controtransfert",
          text: "COSMO aiuta i team a:",
          bullets: [
            "Ridurre il controtransfert negativo",
            "Evitare reazioni impulsive o punitive",
            "Comprendere meglio il mondo interiore del paziente"
          ],
          subtext: "Il risultato è un ambiente in cui i pazienti si sentono compresi, rispettati e coinvolti, rafforzando l'alleanza terapeutica."
        },
        {
          id: 'participation',
          icon: FaBrain,
          title: "Il Paziente come Partecipante Attivo",
          text: "COSMO promuove il coinvolgimento diretto del paziente attraverso:",
          bullets: [
            "Sessioni di feedback strutturate",
            "Attività di riflessione guidata"
          ],
          additionalText: "Questo favorisce:",
          additionalBullets: [
            "Maggiore consapevolezza di sé",
            "Migliori capacità di regolazione emotiva",
            "Una connessione più forte con il team di cura"
          ]
        },
        {
          id: 'team',
          icon: FaUsers,
          title: "Rafforzare il Team, Migliorare la Cura",
          text: "COSMO lavora non solo con i pazienti, ma anche con i team clinici:",
          bullets: [
            "Formazione mirata",
            "Integrazione di dati clinici (test proiettivi, auto-valutazione, osservazioni)",
            "Un framework condiviso tra professionisti"
          ],
          additionalText: "Questo porta a:",
          additionalBullets: [
            "Maggiore coesione",
            "Allineamento nelle strategie di intervento",
            "Chiarezza negli obiettivi terapeutici"
          ]
        },
        {
          id: 'growth',
          icon: FaLightbulb,
          title: "Dalla Gestione del Comportamento alla Crescita",
          text: "COSMO sposta il focus dalla gestione dei comportamenti alla creazione di ambienti che supportano crescita e trasformazione.",
          bullets: [
            "Valutazione accurata",
            "Empatia migliorata",
            "Lavoro di squadra collaborativo"
          ]
        }
      ],

      transformTitle: "Scopri la Differenza COSMO",
      transformText1: "Con strumenti come TCTS, Rorschach e PFS, COSMO aiuta i centri di trattamento a:",
      transformBullets: [
        "Costruire connessioni più profonde con i pazienti",
        "Migliorare i risultati clinici",
        "Favorire cambiamenti duraturi"
      ],
      
      ctaTitle: "Trasforma il Tuo Centro di Trattamento",
      ctaText: "Contattaci per scoprire come COSMO può rivoluzionare il tuo approccio alla cura residenziale e creare cambiamenti positivi duraturi per i tuoi pazienti.",
      ctaButton: "Contatta il Nostro Team"
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-teal-600 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/97cec6254316a951f824fbca4347b65f7f1a7e8bfb3213050d69d6b9d996aba3dd0371de97ad8eb7d82529d579c85e3f/images/aW1hZ2UvcG5n/06162aab33841d1cb736e343b9f79ec0.png"
            alt="Treatment Center Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <motion.h1
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.5}}
            className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
          >
            {currentContent.title}
          </motion.h1>
          <motion.p
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.2, duration: 0.5}}
            className="text-xl md:text-2xl max-w-3xl mx-auto font-medium text-teal-100"
          >
            {currentContent.subtitle}
          </motion.p>
        </div>
      </div>

      {/* Intro Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.4, duration: 0.5}}
          className="bg-white rounded-xl shadow-xl p-8 md:p-10 text-center border border-gray-100"
        >
          {/* ✅ AGGIUNTO: Back to Home Button */}
          <div className="flex justify-center mb-8">
            <Link
              to="/"
              className="flex items-center text-teal-600 hover:text-teal-800 font-semibold transition-colors bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg border border-teal-200"
            >
              <FaHome className="mr-2" />
              {language === 'en' ? 'Back to Home' : 'Torna alla Home'}
            </Link>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed">
            {currentContent.intro}
          </p>
        </motion.div>
      </div>

      {/* Challenge Box */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{opacity: 0, y: 20}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          className="bg-gradient-to-br from-red-50 to-orange-50 border-l-4 border-red-400 rounded-r-xl p-8 mb-12"
        >
          <div className="flex items-start">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <FaShieldAlt className="text-red-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-red-900 mb-3">
                {language === 'en' ? 'The Challenge of Complex Patients' : 'La Sfida dei Pazienti Complessi'}
              </h3>
              <p className="text-red-800 leading-relaxed">
                {language === 'en' 
                  ? "COSMO was created to overcome these challenges, offering tools that enhance empathy, improve understanding of emotional dynamics, and elevate the quality of care."
                  : "COSMO è stato creato per superare queste sfide, offrendo strumenti che migliorano l'empatia, aumentano la comprensione delle dinamiche emotive e innalzano la qualità delle cure."
                }
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {currentContent.sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true, margin: "-50px"}}
              transition={{delay: index * 0.1, duration: 0.5}}
              className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <section.icon className="text-2xl text-teal-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">{section.text}</p>
              
              {section.bullets && (
                <ul className="space-y-2 mb-4">
                  {section.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start">
                      <FaCheckCircle className="text-teal-500 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.additionalText && (
                <>
                  <p className="text-gray-700 font-semibold mt-4 mb-2">{section.additionalText}</p>
                  <ul className="space-y-2 mb-4">
                    {section.additionalBullets.map((bullet, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              
              {section.subtext && (
                <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border-l-4 border-teal-400">
                  {section.subtext}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Transformation Section */}
      <div className="bg-gradient-to-br from-teal-900 to-gray-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-teal-400">
              {currentContent.transformTitle}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              {currentContent.transformText1}
            </p>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 inline-block text-left mb-8">
              <ul className="space-y-3">
                {currentContent.transformBullets.map((bullet, i) => (
                  <li key={i} className="flex items-center text-lg text-gray-200">
                    <FaCheckCircle className="text-teal-500 mr-3" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{currentContent.ctaTitle}</h2>
          <p className="text-xl text-gray-600 mb-10">{currentContent.ctaText}</p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-teal-600 text-white rounded-full font-bold text-lg hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            {currentContent.ctaButton}
            <FaArrowRight className="ml-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TreatmentCenters;